import { Dialog as D } from 'radix-ui'
import type { ReactNode } from 'react'
import { cn } from '../cn.ts'

export const Dialog = D.Root
export const DialogTrigger = D.Trigger
export const DialogClose = D.Close

export interface DialogContentProps {
  title: string
  description?: string
  /** visually hide the title (still read by screen readers) */
  hideTitle?: boolean
  children: ReactNode
  className?: string
  /** 'center' = modal card, 'top' = command-palette position */
  position?: 'center' | 'top'
}

/** Floating modal surface (large panel radius, shadow only for floating elements – §21.1). */
export function DialogContent({
  title,
  description,
  hideTitle,
  children,
  className,
  position = 'center',
}: DialogContentProps) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-40 bg-ink/30 data-[state=open]:animate-[fade-in_150ms_ease-out]" />
      <D.Content
        className={cn(
          'fixed left-1/2 z-50 w-[calc(100vw-32px)] max-w-lg -translate-x-1/2 rounded-panel border border-line-1 bg-card shadow-3',
          'focus:outline-none',
          position === 'center' ? 'top-1/2 -translate-y-1/2 p-6' : 'top-[12vh]',
          className,
        )}
        aria-describedby={description ? undefined : undefined}
      >
        <D.Title className={cn('text-[17px] font-medium text-text-1', hideTitle && 'sr-only')}>
          {title}
        </D.Title>
        {description ? (
          <D.Description className="mt-1 text-[13px] text-text-2">{description}</D.Description>
        ) : (
          <D.Description className="sr-only">{title}</D.Description>
        )}
        {children}
      </D.Content>
    </D.Portal>
  )
}
